import React, { useState, useEffect, useRef } from 'react';
import { CalendarDays } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

const today = new Date();
const minAge18Date = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
const minYear = 1930;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function parseDobToDate(dob) {
  if (!dob) return null;
  const [y, m, d] = dob.split('-').map(Number);
  if (y && m && d) return new Date(y, m - 1, d);
  return null;
}

function formatDateToIso(d, m, y) {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export function isAtLeast18(isoDate) {
  if (!isoDate) return false;
  const dob = parseDobToDate(isoDate);
  if (!dob) return false;
  return dob <= minAge18Date;
}

function isValidCalendarDate(d, m, y) {
  if (y < minYear) return false;
  if (m < 1 || m > 12) return false;
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return false;
  return date <= minAge18Date;
}

export default function DateOfBirthPicker({ value, onChange, className }) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date(minAge18Date.getFullYear(), 0));
  const inputRef = useRef(null);

  useEffect(() => {
    if (value) {
      const [y, m, d] = value.split('-');
      if (y && m && d) {
        setText(`${d}/${m}/${y}`);
        setCalMonth(new Date(Number(y), Number(m) - 1));
      }
    }
  }, [value]);

  const handleTextChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    let formatted = '';
    for (let i = 0; i < raw.length && i < 8; i++) {
      if (i === 2 || i === 4) formatted += '/';
      formatted += raw[i];
    }
    setText(formatted);

    if (raw.length === 8) {
      const d = parseInt(raw.substring(0, 2), 10);
      const m = parseInt(raw.substring(2, 4), 10);
      const y = parseInt(raw.substring(4, 8), 10);
      // Accept valid calendar dates even if under 18 — form-level validation catches it
      const date = new Date(y, m - 1, d);
      if (date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d && y >= minYear) {
        onChange(formatDateToIso(d, m, y));
        setCalMonth(new Date(y, m - 1));
      } else {
        onChange('');
      }
    } else {
      onChange('');
    }
  };

  const handleCalendarSelect = (date) => {
    if (!date) return;
    if (date > minAge18Date) return; // Extra safety
    const iso = formatDateToIso(date.getDate(), date.getMonth() + 1, date.getFullYear());
    onChange(iso);
    setText(`${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`);
    setCalMonth(date);
    setOpen(false);
  };

  const selectedDate = parseDobToDate(value);
  const maxSelectableYear = minAge18Date.getFullYear();

  // Filter months: if viewing the max year, only show months up to the cutoff month
  const availableMonths = MONTHS.map((m, i) => ({ name: m, index: i })).filter(({ index }) => {
    if (calMonth.getFullYear() < maxSelectableYear) return true;
    if (calMonth.getFullYear() === maxSelectableYear) return index <= minAge18Date.getMonth();
    return false;
  });

  return (
    <div className={`flex gap-2 ${className || ''}`}>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        placeholder="DD/MM/YYYY"
        value={text}
        onChange={handleTextChange}
        maxLength={10}
        className="flex-1 bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
        data-testid="input-profile-dob"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="px-3 py-3 bg-cream-50 border border-cream-100 rounded-xl hover:border-[#F4C2C2] transition-colors"
            data-testid="dob-calendar-btn"
          >
            <CalendarDays size={18} className="text-[#78716C]" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white rounded-2xl shadow-xl border border-cream-100" align="end">
          <div className="p-3">
            {/* Year / Month dropdowns */}
            <div className="flex gap-2 mb-3">
              <select
                value={calMonth.getMonth()}
                onChange={(e) => setCalMonth(new Date(calMonth.getFullYear(), Number(e.target.value)))}
                className="flex-1 bg-cream-50 border border-cream-100 rounded-lg px-2 py-1.5 text-sm font-body text-[#2C2A29] focus:outline-none focus:ring-1 focus:ring-blush-200"
                data-testid="dob-month-select"
              >
                {availableMonths.map(({ name, index }) => <option key={name} value={index}>{name}</option>)}
              </select>
              <select
                value={calMonth.getFullYear()}
                onChange={(e) => {
                  const newYear = Number(e.target.value);
                  let newMonth = calMonth.getMonth();
                  // Clamp month if switching to max year
                  if (newYear === maxSelectableYear && newMonth > minAge18Date.getMonth()) {
                    newMonth = minAge18Date.getMonth();
                  }
                  setCalMonth(new Date(newYear, newMonth));
                }}
                className="w-24 bg-cream-50 border border-cream-100 rounded-lg px-2 py-1.5 text-sm font-body text-[#2C2A29] focus:outline-none focus:ring-1 focus:ring-blush-200"
                data-testid="dob-year-select"
              >
                {Array.from({ length: maxSelectableYear - minYear + 1 }, (_, i) => maxSelectableYear - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <DayPicker
              mode="single"
              selected={selectedDate}
              onSelect={handleCalendarSelect}
              month={calMonth}
              onMonthChange={(m) => {
                // Prevent navigating past the 18+ cutoff
                if (m > new Date(maxSelectableYear, minAge18Date.getMonth() + 1, 0)) return;
                setCalMonth(m);
              }}
              toDate={minAge18Date}
              fromYear={minYear}
              toYear={maxSelectableYear}
              showOutsideDays
              className="!m-0"
              classNames={{
                months: "flex flex-col",
                month: "space-y-2",
                caption: "hidden",
                table: "w-full border-collapse",
                head_row: "flex",
                head_cell: "text-[#A89F91] rounded-md w-9 font-normal text-xs",
                row: "flex w-full mt-1",
                cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#FFF5F5] [&:has([aria-selected])]:rounded-md",
                day: "h-9 w-9 p-0 font-normal text-sm rounded-md hover:bg-cream-50 transition-colors inline-flex items-center justify-center cursor-pointer",
                day_selected: "!bg-[#F4C2C2] !text-[#2C2A29] font-semibold",
                day_today: "bg-cream-50 font-semibold",
                day_outside: "text-[#D5CFC7] opacity-50",
                day_disabled: "text-[#D5CFC7] opacity-30 cursor-not-allowed",
              }}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
